<template>
    <div v-if="isInitializing">
        <span class="fa fa-spinner fa-spin"></span>
    </div>
    <div v-else>
        <div v-if="error.length" class="alert alert-danger">
            {{error}}
        </div>

        <div v-if="areAllColumnsInactive" class="alert alert-info">
            Er zijn geen kolommen actief om te tonen
        </div>
        <div v-if="!orders.length && !error.length" class="alert alert-light">
            <p v-if="isLoading">
                <i><span class="fa fa-spinner fa-spin"></span> {{ $t('omt.gui.general.label.is-searching') }}...</i>
                <b-button variant="link" size="sm" title="Annuleren" class="ml-3 p-0" :class="{'opacity-1': !showCancelButton}"
                          @mouseenter="showCancelButton = true" @mouseleave="showCancelButton = false"
                          @click="cancelSearch"
                >
                    <font-awesome-icon icon="ban" class="text-danger"/>
                </b-button>
            </p>
            <i v-else>{{ $t('omt.gui.general.label.no-results') }}</i>
        </div>
        <div v-else>
            <!-- DESKTOP -->
            <b-checkbox-group v-model="selectedOrders">
            <table class="d-none d-md-table d-lg-table d-xl-table table orderlist">
                <thead>
                    <tr>
                        <th v-if="hasBatchActions" class="pr-0">
                            <b-button v-if="!selectedOrders.length" variant="link" class="p-0 ml-2" title="Alles selecteren" @click="toggleSelection()">
                                <font-awesome-icon icon="far fa-check-square" class="text-secondary"/>
                            </b-button>
                            <b-button v-else variant="link" class="p-0 ml-2" title="Alles deselecteren" @click="toggleSelection()">
                                <font-awesome-icon icon="far fa-square" class="text-secondary"/>
                            </b-button>

                            <b-dropdown size="sm" variant="light" :disabled="!isBatchActionMenuEnabled">
                                <template #button-content><font-awesome-icon icon="bars"/></template>
                                <b-dropdown-item v-for="(batchAction, batchActionIdentifier) in batchActions" :disabled="!!currentExecutionBatchActionIdentifier" :key="batchActionIdentifier">
                                    <b-overlay :show="currentExecutionBatchActionIdentifier === batchActionIdentifier" size="sm">
                                        <b-button variant="link" size="sm"
                                            @click="executeBatchAction(batchActionIdentifier)"
                                        >
                                            <span v-if="typeof batchAction.title === 'function'">{{ batchAction.title() }}</span>
                                            <span v-else>{{ batchAction.title }}</span>
                                        </b-button>
                                    </b-overlay>
                                </b-dropdown-item>
                            </b-dropdown>
                        </th>
                        <th v-for="(column, index) in columnsFiltered" :key="index">
                            {{ $t(column.name) }}
                            <a v-if="column.property === filters.sort_by" href="#" @click.prevent="toggleSortDirection" class="lead ml-2 align-middle">
                                <sup v-if="filters.sort_dir === 'desc'" class="fa fa-sort-down align-text-bottom"></sup>
                                <sub v-else class="fa fa-sort-up align-middle"></sub>
                            </a>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(order, index) in orders" class="clickable" @click="getOrderDetailRoute(order)" :key="index">
                        <td v-if="hasBatchActions">
                            <b-checkbox :value="order"/>
                        </td>
                        <td v-for="(column, index) in columnsFiltered"
                            :class="{ 'text-nowrap': column.no_wrap }"
                            :key="index"
                            class="p-0"
                        >
                            <router-link
                                :to="getOrderDetailRoute(order)" class="d-inline-block p-2"
                                :class="{
                                            'text-decoration-line-through': ( isOrderCancelled({ salesOrder: order }) && strikeThroughCancelledOrders ),
                                            'text-blue': (colorizeDealerChanges && dealerHasChanged({ salesOrder: order })),
                                            'text-dark': (!colorizeDealerChanges || !dealerHasChanged({ salesOrder: order })),
                                        }
                                        "

                                >
                                    <template v-if="column.identifier === 'orderIdentifier'">
                                        <FontAwesomeIcon v-if="useIconForCancelledOrder && isOrderCancelled({ salesOrder: order })" icon="ban" class="text-danger mr-1" title="Order volledig geannuleerd" />
                                        <FontAwesomeIcon v-if="useIconForDealerChanges && dealerHasChanged({ salesOrder: order })" icon="info" class="text-blue mr-2" title="Dealerchange heeft plaatsgevonden" />
                                    </template>

                                    <span v-if="column.postProcess?.(order[column.property])" v-html="$t(column.postProcess?.(order[column.property]))"/>
                                    <span v-else>&nbsp;</span>
                            </router-link>
                        </td>
                    </tr>
                    <tr v-if="isLoading">
                        <td :colspan="columnsFiltered.length"><span class="fa fa-spinner fa-spin"></span> {{ $t('omt.gui.general.label.is-searching') }}...</td>
                    </tr>
                </tbody>
            </table>

            <!-- MOBILE -->
            <div class="d-md-none d-lg-none d-xl-none modal-content mt-3" v-for="(order, index) in orders" :key="index">
                <div class="modal-header">
                    <strong>
                        <router-link tag="button" class="btn btn-sm btn-primary mr-2" :to="getOrderDetailRoute(order)"><span class="fa fa-eye"></span></router-link>
                        Order {{order.order_identifier}}
                    </strong>
                </div>
                <div class="modal-body p-2">
                    <table class="table table-borderless table-sm">
                        <tr v-for="(column, index) in columnsFiltered" :key="index">
                            <th>{{ $t(column.name) }}</th>
                            <td>
                                <FontAwesomeIcon v-if="useIconForCancelledOrder && column.identifier === 'orderIdentifier' && isOrderCancelled({ salesOrder: order })" icon="ban" class="text-danger mr-1" />
                                {{ $t(column.postProcess?.(order[column.property])) }}
                            </td>
                        </tr>
                    </table>
                </div>
            </div>
            </b-checkbox-group>
        </div>

        <infinite-scroll :show="!isLoading && !isLastPage && !error.length && !areAllColumnsInactive && !isCancelled" @next="search"/>
    </div>
</template>

<script>
import OrderFilterSet from "../models/OrderFilterSet";
import _ from "lodash"
import DateHelper from "date-helper-util" // https://www.npmjs.com/package/date-helper-util
import GenericHelpers from "../helpers/generic-helpers"
import { isOrderCancelled } from "../helpers/salesOrders"
import InfiniteScroll from "./InfiniteScroll";
import OrderListColumns from "./OrderListColumns";
import i18n from "../views/OrderLists/OrderSearch.i18n"

export default {
    name: "OrderList",
    components: {InfiniteScroll},

    model: {
        prop: 'filters',
        event: 'input',
    },

    props: {
        filters: {required: true, type: Object},
        columns: {required: true, type: Array},
        resultsType: {required: false, type: String, default: 'sales_orders'},
    },
    
    data() {
        return {
            isLoading: true,
            isLastPage: false,
            isCancelled: false,
            currentExecutionBatchActionIdentifier: null,
            showCancelButton: false,
            error: '',
            normalizedFilters: {},
            orders: [],
            axiosAbortController: new AbortController,
            selectedOrders: [],
        }
    },

    i18n,

    mounted() {
        this.$emit('searching', this.isLoading)
        OrderListColumns.preload()
        this.search()
    },
    
    computed: {
        isInitializing() {
            return !OrderListColumns.isPreloaded()
        },
        columnsFiltered() {
            return _.defaultsDeep(this.columns, OrderListColumns.columns)
                .filter(column => column.is_active)
        },
        areAllColumnsInactive() {
            return this.columns.filter(col => !col.is_active).length === this.columns.length
        },
        batchActions() {
            let actions = {
                ...OrderListColumns.batchActionsPerSearchType.global,
                ...OrderListColumns.batchActionsPerSearchType[this.resultsType],
            }

            for (let actionKey in actions) {
                if (!actions[actionKey].show()) {
                    delete actions[actionKey]
                }
            }

            return actions
        },
        hasBatchActions() {
            return !!Object.keys(this.batchActions ?? {}).length
        },
        isBatchActionMenuEnabled() {
            return this.hasBatchActions && this.selectedOrders.length
        },
        useIconForCancelledOrder() {
            return this.$store.getters.getFrontendParameterValue('order-search', 'cancelled-orders.show-icon');
        },
        strikeThroughCancelledOrders() {
            return this.$store.getters.getFrontendParameterValue('order-search', 'cancelled-orders.strikethrough');
        },
        useIconForDealerChanges() {
            return this.$store.getters.getFrontendParameterValue('order-search', 'dealer-changes.show-icon');
        },
        colorizeDealerChanges() {
            return this.$store.getters.getFrontendParameterValue('order-search', 'dealer-changes.colorize');
        },

    },
    
    methods: {
        isOrderCancelled,
        search() {
            this.normalizeFilters()

            this.isLoading = true
            this.error = ''
            this.normalizedFilters.offset = this.orders.length
            this.axiosAbortController = new AbortController

            this.$http.get(`/api/rest/sales-orders-search?${GenericHelpers.toQueryString(this.normalizedFilters)}`, {signal: this.axiosAbortController.signal})
                .then(response => {
                    if (response.data.length < this.normalizedFilters.limit) {
                        this.isLastPage = true
                    }

                    this.orders = this.orders.concat(
                        response.data
                            .map(so => {
                                so.dispatch_requests = so.dispatch_requests ?? []
                                so.dispatch_requests = so.dispatch_requests.concat(so.current_dispatch_requests_with_paused ?? [])
                                so.dispatch_requests = so.dispatch_requests.filter(drq => {
                                    const responsibleLocation = this.$store.state.allLocations.find(l => l.id === drq.responsible_location_id)

                                    return this.hasLocation(drq.dispatcher_type, drq.dispatcher_identifier)
                                        || this.hasLocation(responsibleLocation?.location_type?.identifier, responsibleLocation?.identifier)
                                })

                                so.dispatch_requests.sort((a,b) => {
                                    if (a.dispatch_action === 'crossdocking') return -1
                                    return 0
                                })

                                so.dispatch_requests.sort((a,b) => {
                                    if (a.status !== 'cancelled') return -1;
                                    return 1;
                                })

                                return so
                            })
                            .filter(so => {
                                if (!so.dispatch_requests.length && !this.hasPermission('canmanagealllocations')) {
                                    console.error(`Order '${so.order_identifier}' was hidden because the user is linked to none if it's active DRQs.`)
                                    return false
                                }

                                return true
                            })
                    )
                })
                .catch(e => {
                    console.error(e)
                    if (!this.isCancelled) {
                        this.error = 'Er is iets misgegaan bij het zoeken. Probeer het opnieuw.'
                    }
                })
                .then(() => {
                    this.isLoading = false
                })
        },
        normalizeFilters() {
            this.normalizedFilters = _.defaultsDeep(_.cloneDeep(this.filters), OrderFilterSet)

            // Normalize dates
            if (!this.normalizedFilters.order_placed_to) {
                this.normalizedFilters.order_placed_to = DateHelper(new Date()).getFormated('YYYY-MM-DD')
            }

            if (this.normalizedFilters.max_age_days) {
                this.normalizedFilters.max_age_days = parseInt(this.normalizedFilters.max_age_days)

                if (!isNaN(this.normalizedFilters.max_age_days)) {
                    let newStartDate = DateHelper(this.normalizedFilters.order_placed_to, 'YYYY-MM-DD')
                    if (this.normalizedFilters.max_age_days) {
                        newStartDate.sub("day", this.normalizedFilters.max_age_days)
                    }

                    if (!this.normalizedFilters.order_placed_from || newStartDate.isAfter(DateHelper(this.normalizedFilters.order_placed_from, 'YYYY-MM-DD').getDateObject())) {
                        this.normalizedFilters.order_placed_from = newStartDate.getFormated('YYYY-MM-DD')
                    }
                }
            }

            if (this.normalizedFilters.responsible_location_type && !this.normalizedFilters.responsible_location_identifier) {
                this.normalizedFilters.responsible_location_type = null
            }

            const address_fields = ['type', 'name', 'city', 'zipcode', 'email', 'country']
            this.normalizedFilters.address = {}
            address_fields.forEach((field) => {
                if (this.normalizedFilters[`address_${field}`]) {
                    this.normalizedFilters.address[field] = this.normalizedFilters[`address_${field}`]
                }
                delete this.normalizedFilters[`address_${field}`]
            })

            // Filter empty values
            this.normalizedFilters = _.forIn(this.normalizedFilters, (value, key) => {
                if (value === '') delete this.normalizedFilters[key]
            })

            if (this.normalizedFilters.order_identifier) {
                let excludeFieldsFromDisable = ['order_identifier', 'sort_by', 'sort_dir', 'offset', 'limit', 'include']
                if (!this.hasPermission('canmanagealllocations')) {
                    excludeFieldsFromDisable = excludeFieldsFromDisable.concat(['responsible_location_type', 'responsible_location_identifier', 'dispatcher_type', 'dispatcher_identifier'])
                }

                for (let i in this.normalizedFilters) {
                    if (!excludeFieldsFromDisable.includes(i)) {
                        this.normalizedFilters[i] = ''
                    }
                }
                this.normalizedFilters.order_placed_from = '1111-11-11'
            }
        },
        getOrderDetailRoute(order) {
            try {
                this.error = ""

                let route = {
                    params: {
                        salesOrderIdentifier: order.order_identifier,
                    },
                }

                switch (this.resultsType) {
                    case 'pick_by_store':
                        route.name = 'OrderPickByStoreScan'
                        const dispatchRequest = order.dispatch_requests
                            ?.find(dr => dr.status === 'requested' && dr.dispatcher_type === 'store');

                        (typeof dispatchRequest !== 'undefined')
                            ? route.params.dispatchRequestId = dispatchRequest.id
                            : route.name = 'OrderDetailOverview';

                        break
                    case 'fraud_review':
                        route.name = 'OrderDetailHoldReasons'
                        break
                    case 'refunds':
                        route.name = 'OrderDetailRefunds'
                        break
                    default:
                        route.name = 'OrderDetailOverview'
                        break
                }

                return route
            } catch (e) {
                console.error(e)
                return {}
                // this.error = "Kan niet navigeren naar order detail. Neem contact op met OmniStock"
            }
        },
        toggleSortDirection() {
            this.filters.sort_dir = (this.filters.sort_dir === 'desc') ? 'asc' : 'desc'
            this.orders = []
            this.search()
        },
        getStoreDispatchRequestForOrder(order) {
            let storeDR = order.dispatch_requests.filter(dr => dr.dispatcher_type === 'store')[0]
            return (typeof storeDR !== 'undefined')
                ? storeDR
                : null
        },
        cancelSearch() {
            this.isCancelled = true
            this.axiosAbortController.abort()
            this.$http.get(`/api/rest/sales-orders-search/cancel`)
                .then(response => {})
            this.orders = []
            this.isLoading = false
        },
        executeBatchAction(batchActionIdentifier) {
            this.currentExecutionBatchActionIdentifier = batchActionIdentifier
            this.batchActions[batchActionIdentifier].execute(this.selectedOrders)
                .catch(e => {
                    console.error([e])
                    this.$alert('Er is iets misgegaan. Probeer het opnieuw.', 'danger')
                })
                .then(() => this.currentExecutionBatchActionIdentifier = null)
        },
        dealerHasChanged({ salesOrder }) {
            if (!this.useIconForDealerChanges && !this.colorizeDealerChanges) return false; // only use this functionality if either one of these settings are in effect

            try {
                const currentDispatcherIdentifierForOrder = salesOrder["current_dispatch_requests_with_paused"]
                                                        .filter(drq => drq.dispatcher_type === "store")[0]["dispatcher_identifier"];

                if (salesOrder["fulfilment_location_identifier"] === null) return false;

                const originalDispatcherIdentifiersForOrder = salesOrder["fulfilment_location_identifier"];

                return currentDispatcherIdentifierForOrder !== originalDispatcherIdentifiersForOrder;
            } catch { return false; }
        },
        toggleSelection() {
            if (this.selectedOrders.length > 0) {
                this.selectedOrders = []
                return
            }

            this.selectedOrders = []
            _.each(this.orders, (order) => {
                this.selectedOrders.push(order)
            })
        },
        dealerHasChanged({ salesOrder }) {
            if (!this.useIconForDealerChanges && !this.colorizeDealerChanges) return false; // only use this functionality if either one of these settings are in effect

            try {
                const currentDispatcherIdentifierForOrder = salesOrder["current_dispatch_requests_with_paused"]
                                                        .filter(drq => drq.dispatcher_type === "store")[0]["dispatcher_identifier"];

                if (salesOrder["fulfilment_location_identifier"] === null) return false;

                const originalDispatcherIdentifiersForOrder = salesOrder["fulfilment_location_identifier"];

                return currentDispatcherIdentifierForOrder !== originalDispatcherIdentifiersForOrder;
            } catch { return false; }
        },
    },

    beforeDestroy() {
        this.cancelSearch()
    },

    watch: {
        isLoading(newVal) {
            this.$emit('searching', newVal)
        },
    },
}
</script>

<style scoped lang="scss">
.orderlist {
    a {
        &:hover {
            text-decoration: none;
        }
    }
    .text-decoration-line-through { // class name adopted from Bootstrap v5.0.2
        text-decoration: line-through;
    }
    .text-blue {
        color: #2e709f !important;
    }
}
</style>
